<template>
	<div>
		<!-- Main Rate Limit Alert -->
		<v-slide-y-transition>
			<v-alert
				v-if="isRateLimited && !rateLimitDismissed && !isExempt"
				prominent
				:type="isLockedOut ? 'error' : 'warning'"
				elevation="4"
				class="rate-limit-notification"
				:color="isLockedOut ? 'red darken-2' : 'orange darken-1'"
				dark
				dismissible
				@input="dismissRateLimit"
			>
				<div class="text-h6 mb-2">
					<v-icon left>{{ isLockedOut ? "mdi-lock-alert" : "mdi-clock-alert-outline" }}</v-icon>
					{{ isLockedOut ? "Account Temporarily Locked" : "Search Rate Limit Reached" }}
				</div>
				<div class="text-h6 mb-3">
					<span v-if="isLockedOut">
						Too many requests detected. Your account has been temporarily locked for
						<strong>{{ lockoutDuration }} seconds</strong>
						.
					</span>
					<span v-else>
						You've exceeded the maximum number of searches allowed. Please wait
						<strong>{{ formattedTime }}</strong>
						before trying again.
					</span>
				</div>
				<div v-if="violatedLimit" class="text-caption mb-2">
					<v-icon small left>mdi-information</v-icon>
					Violated limit: {{ violatedLimit }}
				</div>

				<!-- Progress Bar -->
				<v-progress-linear
					:value="progressPercentage"
					color="white"
					background-color="orange darken-3"
					height="25"
					rounded
					class="mb-2"
				>
					<template v-slot:default>
						<strong>{{ formattedMinutes }}:{{ formattedSeconds }}</strong>
					</template>
				</v-progress-linear>

				<!-- Queue Status -->
				<div v-if="hasQueuedRequests" class="text-caption">
					<v-icon small left>mdi-playlist-play</v-icon>
					{{ queuedRequestCount }} request{{ queuedRequestCount !== 1 ? "s" : "" }} queued for retry
				</div>
			</v-alert>
		</v-slide-y-transition>

		<!-- Warning Alert (Approaching Limit) -->
		<v-slide-y-transition>
			<v-alert
				v-if="showWarning && !isRateLimited && !isExempt"
				dense
				type="info"
				elevation="2"
				class="rate-limit-warning"
				color="blue-grey darken-1"
				dark
				dismissible
				@input="dismissWarning"
			>
				<v-row align="center" no-gutters>
					<v-col>
						<v-icon left small>mdi-information-outline</v-icon>
						<span class="text-body-2">
							<strong>Heads up!</strong>
							You have
							<strong>{{ requestsRemaining }}</strong>
							search{{ requestsRemaining !== 1 ? "es" : "" }} remaining before rate limit.
						</span>
					</v-col>
				</v-row>
			</v-alert>
		</v-slide-y-transition>

		<!-- Inline Search Warning -->
		<v-fade-transition>
			<v-chip
				v-if="showRemainingChip && !isExempt"
				color="orange"
				text-color="white"
				small
				class="rate-limit-chip ma-2"
				@click="dismissChip"
			>
				<v-icon left small>mdi-alert</v-icon>
				{{ requestsRemaining }} searches left
				<v-icon right small>mdi-close</v-icon>
			</v-chip>
		</v-fade-transition>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
	name: "RateLimitNotification",

	data() {
		return {
			warningDismissed: false,
			chipDismissed: false,
			chipDismissTimestamp: null,
			rateLimitDismissed: false,
		};
	},

	computed: {
		...mapGetters({
			isRateLimited: "rateLimiting/isRateLimited",
			isLockedOut: "rateLimiting/isLockedOut",
			lockoutDuration: "rateLimiting/lockoutDuration",
			violatedLimit: "rateLimiting/violatedLimit",
			remainingTime: "rateLimiting/remainingTime",
			lastRateLimitTrigger: "rateLimiting/lastRateLimitTrigger",
			progressPercentage: "rateLimiting/progressPercentage",
			formattedTime: "rateLimiting/formattedTime",
			isWarning: "rateLimiting/isWarning",
			requestsRemaining: "rateLimiting/requestsRemaining",
			hasQueuedRequests: "rateLimiting/hasQueuedRequests",
			queuedRequestCount: "rateLimiting/queuedRequestCount",
			rateLimitMessage: "rateLimiting/rateLimitMessage",
			isLoggedIn: "isLoggedIn",
			isExempt: "isUserRateLimitExempt",
		}),

		formattedMinutes() {
			const minutes = Math.floor(this.remainingTime / 60);
			return minutes.toString().padStart(2, "0");
		},

		formattedSeconds() {
			const seconds = this.remainingTime % 60;
			return seconds.toString().padStart(2, "0");
		},

		showWarning() {
			return this.isWarning && !this.warningDismissed && this.requestsRemaining <= 3;
		},

		showRemainingChip() {
			// Don't show if already rate limited or chip was dismissed
			if (this.isRateLimited || this.chipDismissed) {
				return false;
			}

			// Only show if we have a valid remaining count <= 5
			return this.requestsRemaining !== null && this.requestsRemaining <= 5 && this.requestsRemaining > 0;
		},
	},

	methods: {
		...mapActions({
			clearRateLimit: "rateLimiting/clearRateLimit",
		}),

		dismissRateLimit(value) {
			if (!value) {
				this.rateLimitDismissed = true;
				// Don't auto-reset the dismissal - it will reset when a new rate limit occurs
			}
		},

		dismissWarning(value) {
			if (!value) {
				this.warningDismissed = true;
				// Reset after 5 minutes
				setTimeout(() => {
					this.warningDismissed = false;
				}, 5 * 60 * 1000);
			}
		},

		dismissChip() {
			this.chipDismissed = true;
			this.chipDismissTimestamp = Date.now();

			// Auto-reset chip dismissal after 30 seconds
			setTimeout(() => {
				this.chipDismissed = false;
				this.chipDismissTimestamp = null;
			}, 30 * 1000);
		},

		handleRateLimitTriggered() {
			// When a new rate limit is triggered, reset the dismissal
			// to ensure the notification is shown again
			if (this.isRateLimited && this.rateLimitDismissed) {
				console.log("New rate limit trigger detected, showing notification again");
				this.rateLimitDismissed = false;
			}
		},
	},

	watch: {
		// Reset dismissals when rate limit state changes
		isRateLimited(newVal, oldVal) {
			if (!newVal) {
				// Rate limit cleared
				this.warningDismissed = false;
				this.chipDismissed = false;
				this.chipDismissTimestamp = null;
				this.rateLimitDismissed = false;
			} else if (newVal && !oldVal) {
				// New rate limit occurred - reset dismissal so notification shows again
				this.rateLimitDismissed = false;
			}
		},

		// Reset dismissal if lockout duration increases (escalation)
		lockoutDuration(newVal, oldVal) {
			if (newVal > oldVal && this.isRateLimited) {
				// Lockout duration increased - show notification again
				this.rateLimitDismissed = false;
			}
		},

		// Watch for new rate limit triggers while already rate limited
		lastRateLimitTrigger(newVal, oldVal) {
			if (newVal && oldVal && newVal !== oldVal && this.isRateLimited) {
				// A new rate limit request was attempted while already rate limited
				// Reset the dismissal to show the notification again
				this.rateLimitDismissed = false;
			}
		},

		// Auto-dismiss warning after showing
		showWarning(newVal) {
			if (newVal) {
				setTimeout(() => {
					this.warningDismissed = true;
				}, 10000); // Auto-dismiss after 10 seconds
			}
		},

		// Reset chip when requestsRemaining resets to null or goes above threshold
		requestsRemaining(newVal, oldVal) {
			// If requests remaining is cleared (null) or goes above 5, reset chip dismissal
			if (newVal === null || newVal > 5) {
				this.chipDismissed = false;
				this.chipDismissTimestamp = null;
			}

			// If we went from low to high remaining, it means rate limit window reset
			if (oldVal !== null && oldVal <= 5 && (newVal === null || newVal > 5)) {
				// Clear any lingering rate limit state
				if (this.isRateLimited) {
					this.clearRateLimit();
				}
			}
		},
	},

	mounted() {
		// Listen for rate limit trigger events
		this.$rateLimitBus.$on("rate-limit-triggered", this.handleRateLimitTriggered);
	},

	beforeDestroy() {
		// Clean up event listener
		this.$rateLimitBus.$off("rate-limit-triggered", this.handleRateLimitTriggered);
		// Clean up when component is destroyed
		this.clearRateLimit();
	},
};
</script>

<style lang="scss" scoped>
.rate-limit-notification {
	position: fixed;
	top: 64px; // Below navbar
	left: 16px;
	right: 16px;
	z-index: 99999;
	max-width: 900px;
	margin: 0 auto;

	@media (max-width: 600px) {
		top: 56px; // Adjust for mobile navbar
		left: 8px;
		right: 8px;
	}
}

.rate-limit-warning {
	position: fixed;
	bottom: 24px;
	right: 24px;
	max-width: 400px;
	z-index: 9998;

	@media (max-width: 600px) {
		bottom: 16px;
		left: 16px;
		right: 16px;
		max-width: none;
	}
}

.rate-limit-chip {
	position: fixed;
	bottom: 24px;
	left: 24px;
	z-index: 9997;
	animation: pulse 2s infinite;
	cursor: pointer;
	transition: transform 0.2s ease;

	&:hover {
		transform: scale(1.05);
	}

	@media (max-width: 600px) {
		bottom: 70px; // Above mobile navigation if present
	}
}

@keyframes pulse {
	0% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.05);
		opacity: 0.9;
	}
	100% {
		transform: scale(1);
		opacity: 1;
	}
}

// Dark theme adjustments
.theme--dark {
	.rate-limit-notification {
		.v-alert__content {
			color: #fff !important;
		}
	}
}

// Animation for queue status
.v-icon {
	&.mdi-playlist-play {
		animation: bounce 1s infinite;
	}
}

@keyframes bounce {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-3px);
	}
}
</style>
